import React, { useState, useEffect, useRef, useId } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";
import { getRestaurantNotificationsList, createRestaurantNotificationsList } from "../../actions/restaurantActions";
import { api } from "../../App";
import { ConfirmationModal } from "../ConfirmationModal";
import PhoneField from "../PhoneField";
import { LuMessageCircle, LuMail, LuPlus, LuTrash2, LuCheck, LuClock3, LuShieldCheck, LuUser } from "react-icons/lu";
import { OverviewButton } from "../overview/overviewStyles";

function InlineContactForm({ restaurantId, kind, title }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [countryCode, setCountryCode] = useState("351");
  const [phone, setPhone] = useState("");
  const [phoneValid, setPhoneValid] = useState(false);
  const [pending, setPending] = useState(false);
  const [feedback, setFeedback] = useState("");
  const inFlight = useRef(false);
  const nameInput = useRef(null);
  const phoneId = useId();
  const feedbackId = useId();
  const isEmail = kind === "email";
  const clearFeedback = () => setFeedback("");
  const submit = async event => {
    event.preventDefault();
    if (inFlight.current || !restaurantId) return;
    if (isEmail ? !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) : !phone.trim() || !phoneValid) {
      setFeedback(isEmail ? "invalidEmail" : "invalidPhone");
      return;
    }
    inFlight.current = true;
    setPending(true);
    clearFeedback();
    try {
      const result = await dispatch(createRestaurantNotificationsList(restaurantId, {
        restaurant: Number(restaurantId),
        name: name.trim(),
        email: isEmail ? email.trim() : "",
        phone_number: isEmail ? "" : phone,
        country_code: isEmail ? "" : countryCode,
        getEmails: isEmail,
        getWhatsappNotifications: !isEmail,
        whatsapp_notification_token: "",
        isItConfirmed: false,
      }));
      if (!result?.success) {
        setFeedback("failed");
        return;
      }
      setName("");
      setEmail("");
      setPhone("");
      setFeedback("added");
      nameInput.current?.focus();
    } catch {
      setFeedback("failed");
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  };
  const invalid = feedback === "invalidEmail" || feedback === "invalidPhone";
  return <ContactForm onSubmit={submit} noValidate aria-label={t("notificationsDesign.addContact") + " — " + title} aria-busy={pending}>
    <ContactField>
      <span>{t("ui.components.notifications.restaurantNotifications.nome")}</span>
      <FieldShell><LuUser aria-hidden="true" /><ContactInput ref={nameInput} type="text" autoComplete="name" placeholder={t("ui.components.notifications.restaurantNotifications.nome2")} value={name} readOnly={pending} onChange={event => { setName(event.target.value); clearFeedback(); }} /></FieldShell>
    </ContactField>
    {isEmail ? <ContactField>
      <span>{t("ui.components.notifications.restaurantNotifications.eMail")}</span>
      <FieldShell><LuMail aria-hidden="true" /><ContactInput type="email" autoComplete="email" required placeholder={t("ui.components.notifications.restaurantNotifications.emailExampleCom")} value={email} readOnly={pending} aria-invalid={invalid} aria-describedby={invalid ? feedbackId : undefined} onChange={event => { setEmail(event.target.value); clearFeedback(); }} /></FieldShell>
    </ContactField> : <ContactField as="div">
      <label htmlFor={phoneId}>{t("ui.components.notifications.restaurantNotifications.telemovel")}</label>
      <PhoneField inputId={phoneId} inputLabel={t("ui.components.notifications.restaurantNotifications.telemovel")} inputInvalid={invalid} describedBy={invalid ? feedbackId : undefined} disabled={pending} countryCodeNumber={countryCode} setCountryCodeNumber={setCountryCode} phoneWithoutCountryCode={phone} setPhoneWithoutCountryCode={value => { setPhone(value); clearFeedback(); }} isPhoneValid={phoneValid} setIsPhoneValid={setPhoneValid} />
    </ContactField>}
    <CreateButton type="submit" $primary disabled={pending || !restaurantId} aria-label={t(pending ? "notificationsInline.adding" : "notificationsDesign.addContact") + " — " + title}>
      <LuPlus aria-hidden="true" />{t(pending ? "notificationsInline.adding" : "notificationsDesign.addContact")}
    </CreateButton>
    {feedback && <InlineFeedback id={feedbackId} role={feedback === "added" ? "status" : "alert"} $success={feedback === "added"}>
      {feedback === "added" && <LuCheck aria-hidden="true" />}
      {t("notificationsInline." + feedback)}
    </InlineFeedback>}
  </ContactForm>;
}

function RestaurantNotifications() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const restaurantId = useSelector(state => state.restaurantDetails.restaurants?.id);
  const { restaurantNotifications: contacts, restaurantNotificationsError } = useSelector(state => state.restaurantNotifications);
  const [deletingContact, setDeletingContact] = useState(null);
  const [deleteError, setDeleteError] = useState(false);
  const deletingRef = useRef(false);
  useEffect(() => {
    if (!restaurantId) return;
    dispatch(getRestaurantNotificationsList(restaurantId));
    const timer = setInterval(() => dispatch(getRestaurantNotificationsList(restaurantId)), 15000);
    return () => clearInterval(timer);
  }, [dispatch, restaurantId]);
  useEffect(() => { setDeletingContact(null); setDeleteError(false); }, [restaurantId]);
  const deleteContact = async () => {
    if (deletingRef.current || !deletingContact) return;
    deletingRef.current = true;
    try {
      await api.delete(`/restaurants/${restaurantId}/restaurant_notifications/${deletingContact.id}/`);
      setDeletingContact(null);
      dispatch(getRestaurantNotificationsList(restaurantId));
    } catch {
      setDeleteError(true);
    } finally {
      deletingRef.current = false;
    }
  };
  const channels = [
    { kind: "whatsapp", title: "WhatsApp", icon: LuMessageCircle, items: (Array.isArray(contacts) ? contacts : []).filter(item => item.phone_number), hint: "whatsappHint", empty: "noWhatsapp" },
    { kind: "email", title: "E-mail", icon: LuMail, items: (Array.isArray(contacts) ? contacts : []).filter(item => item.email), hint: "emailHint", empty: "noEmail" },
  ];
  const closeDelete = () => { setDeletingContact(null); setDeleteError(false); };
  const deletingPhone = Boolean(deletingContact?.phone_number && !deletingContact?.email);
  return <>
    {restaurantNotificationsError && <Feedback role="alert">
      {t("notificationsDesign.loadError")}
      <OverviewButton type="button" onClick={() => dispatch(getRestaurantNotificationsList(restaurantId))}>{t("notificationsDesign.retry")}</OverviewButton>
    </Feedback>}
    <NotificationsGrid>
      {channels.map(channel => <Wrapper key={channel.kind} aria-label={channel.title}>
        <WrapperHeader>
          <HeaderLeft>
            <ChannelIcon><channel.icon aria-hidden="true" /></ChannelIcon>
            <HeaderTitle>{channel.title}</HeaderTitle>
            {Array.isArray(contacts) && <ContactCount aria-label={t("notificationsDesign.contactCount", { count: channel.items.length })}>{channel.items.length}</ContactCount>}
          </HeaderLeft>
        </WrapperHeader>
        <HeaderSub>{t("notificationsDesign." + channel.hint)}</HeaderSub>
        <InlineContactForm key={String(restaurantId) + channel.kind} restaurantId={restaurantId} kind={channel.kind} title={channel.title} />
        <WrapperBot>
          {!Array.isArray(contacts) ? <EmptyContactState role="status">{t(restaurantNotificationsError ? "notificationsDesign.unavailable" : "notificationsDesign.loading")}</EmptyContactState>
          : channel.items.length > 0 ? channel.items.map(notification => <ContactRow key={notification.id}>
            <ContactIdentity>
              {notification.name && <ZoneTitle>{notification.name}</ZoneTitle>}
              <ContactValue>{channel.kind === "email" ? notification.email : notification.phone_number}</ContactValue>
            </ContactIdentity>
            <StatusPill $confirmed={notification.isItConfirmed === true}>
              {notification.isItConfirmed === true ? <LuCheck aria-hidden="true" /> : <LuClock3 aria-hidden="true" />}
              {t(notification.isItConfirmed === true ? "notificationsDesign.confirmed" : "notificationsDesign.pending")}
            </StatusPill>
            <TrashButton type="button" onClick={() => { setDeleteError(false); setDeletingContact(notification); }} aria-label={t("common.delete") + " " + (notification.email || notification.phone_number)}><LuTrash2 aria-hidden="true" /></TrashButton>
          </ContactRow>) : <EmptyContactState>{t("notificationsDesign." + channel.empty)}</EmptyContactState>}
        </WrapperBot>
      </Wrapper>)}
    </NotificationsGrid>
    <VerificationNote><LuShieldCheck aria-hidden="true" />{t("notificationsDesign.verificationNote")}</VerificationNote>
    <ConfirmationModal show={Boolean(deletingContact)} onHide={closeDelete} onConfirm={deleteContact}
      title={t(deletingPhone ? "ui.i18nCleanup2.eliminarNumeroDeTelemovel" : "ui.i18nCleanup2.eliminarEnderecoDeEMail")}
      body={<>{t(deletingPhone ? "ui.i18nCleanup2.tensACertezaQuePretendesDeixarDeReceberNotificacoesNesteNumero" : "ui.i18nCleanup2.tensACertezaQuePretendesDeixarDeReceberNotificacoesNesteEMail")}{deleteError && <InlineFeedback role="alert">{t("notificationsInline.deleteFailed")}</InlineFeedback>}</>}
      cancelLabel={t("ui.components.notifications.restaurantNotifications.cancelar")} confirmLabel={t("common.delete")} confirmColor="#EE754C" cancelColor="#99b0ad" />
  </>;
}
export default RestaurantNotifications;
const NotificationsGrid = styled.div`
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); width: 100%;
  border: 1px solid #e5ece7; border-radius: 12px; background: #ffffff; padding: 24px;
  > section + section { border-left: 1px solid #e7eeea; margin-left: 28px; padding-left: 28px; }
  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr); padding: 20px;
    > section + section { border-left: 0; border-top: 1px solid #e7eeea; margin: 20px 0 0; padding: 20px 0 0; }
  }
  @media (max-width: 540px) { padding: 16px; }
`;
const Wrapper = styled.section`min-width: 0;`;
const WrapperHeader = styled.header`
  display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;
`;
const HeaderLeft = styled.div`
  display: flex; align-items: center; gap: 10px; min-width: 0;
`;
const ChannelIcon = styled.span`
  display: inline-flex; align-items: center; color: #78968a;
  svg { width: 21px; height: 21px; stroke-width: 1.7; }
`;
const HeaderTitle = styled.h2`
  font-size: 16px; font-weight: 650; color: #3e564b; margin: 0; line-height: 1.4;
`;
const ContactCount = styled.span`
  display: inline-flex; justify-content: center; align-items: center; min-width: 23px; height: 23px;
  border-radius: 7px; background: #f0f5f2; padding: 0 6px; color: #6c897b; font-size: 11px; font-weight: 600;
`;
const HeaderSub = styled.p`
  font-size: 12px; color: #788a80; line-height: 1.65; margin: 12px 0 4px;
`;
const CreateButton = styled(OverviewButton)`
  min-height: 42px; padding: 7px 10px; font-size: 11px; flex-shrink: 0;
`;
const WrapperBot = styled.div`min-width: 0;`;
const ContactRow = styled.div`
  display: grid; grid-template-columns: minmax(0, 1fr) auto 32px; align-items: center;
  gap: 12px; padding: 16px 0; border-bottom: 1px solid #edf1ee;
  &:last-child { border-bottom: 0; padding-bottom: 0; }
  @media (max-width: 1150px) {
    grid-template-columns: minmax(0, 1fr) 32px; gap: 7px 10px;
    > button { grid-column: 2; grid-row: 1 / span 2; }
  }
`;
const ContactIdentity = styled.div`min-width: 0;`;
const ZoneTitle = styled.div`
  color: #3c5549; font-size: 13px; font-weight: 650; line-height: 1.5; overflow-wrap: anywhere;
`;
const ContactValue = styled.div`
  color: #708379; font-size: 12px; line-height: 1.6; overflow-wrap: anywhere;
`;
const StatusPill = styled.span`
  display: inline-flex; align-items: center; gap: 5px; justify-self: start; min-height: 25px;
  padding: 4px 7px; border-radius: 6px; font-size: 10px; font-weight: 600;
  color: ${({ $confirmed }) => $confirmed ? "#4e7762" : "#9b793f"};
  background: ${({ $confirmed }) => $confirmed ? "#edf5ef" : "#fcf6e9"};
  svg { width: 13px; height: 13px; flex-shrink: 0; }
`;
const TrashButton = styled(OverviewButton)`
  min-height: 32px; width: 32px; padding: 0; background: transparent; color: #98a69f; border-color: transparent;
  &:hover:not(:disabled) { color: #b75b51; background: #fff3ef; border-color: #f3ded8; }
`;
const EmptyContactState = styled.div`
  padding: 14px 0 2px; color: #8a9890; font-size: 12px; line-height: 1.6;
`;
const VerificationNote = styled.p`
  display: flex; align-items: flex-start; gap: 8px; color: #86958b; font-size: 11px;
  line-height: 1.6; margin: 16px 2px; svg { width: 15px; height: 15px; flex-shrink: 0; margin-top: 1px; }
`;
const Feedback = styled.div`
  display: flex; flex-wrap: wrap; align-items: center; gap: 12px; margin-bottom: 14px;
  color: #a05746; font-size: 13px;
`;
const ContactForm = styled.form`
  display: flex; flex-wrap: wrap; align-items: flex-end; gap: 10px;
  min-width: 0; margin: 18px 0 0; padding-bottom: 18px;
  border-bottom: 1px solid #edf1ee;
  > label { flex: 1 1 160px; }
  > div { flex: 1.3 1 210px; }
  > button { margin-left: auto; }
  > p { flex: 0 0 100%; }
  .react-tel-input { min-width: 0; }
  .react-tel-input input { width: 100% !important; min-width: 0; border-color: #dce6e1; }
  && .react-tel-input input:focus, && .react-tel-input input:focus-visible {
    outline: none; border-color: #7f9e94 !important; box-shadow: 0 0 0 2px rgba(127,158,148,.14) !important;
  }
  .react-tel-input .country-list { max-width: calc(100vw - 70px); }
  @media (max-width: 600px) {
    > label, > div, > button { flex-basis: 100%; width: 100%; }
  }
`;
const ContactField = styled.label`
  display: flex; flex-direction: column; gap: 7px; margin: 0; min-width: 0;
  > span:first-child, > label { font-size: 12px; color: #647a6e; font-weight: 600; margin: 0; }
`;
const FieldShell = styled.span`
  display: flex; align-items: center; gap: 10px; min-height: 42px; padding: 0 12px;
  border: 1px solid #dce6e1; border-radius: 8px; background: white;
  > svg { width: 17px; height: 17px; color: #87a08f; flex-shrink: 0; }
  &:focus-within { border-color: #7f9e94; box-shadow: 0 0 0 2px rgba(127,158,148,.14); }
`;
const ContactInput = styled.input`
  width: 100%; min-width: 0; height: 40px; background: transparent; color: #354f41;
  font: inherit; font-size: 14px; padding: 0;
  &&, &&:focus, &&:focus-visible { border: 0; outline: none; box-shadow: none; }
  &::placeholder { color: #9ba99f; }
  @media (max-width: 600px) { font-size: 16px; }
`;
const InlineFeedback = styled.p`
  display: flex; align-items: flex-start; gap: 6px; margin: 2px 0 0; line-height: 1.6;
  font-size: 12px; color: ${({ $success }) => $success ? "#4e7762" : "#b15649"};
  svg { width: 15px; height: 15px; flex-shrink: 0; margin-top: 2px; }
`;
